import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, MessageCircle, Users, Send, UserX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const G = {
  bg:         '#050508',
  surface:    '#0d0d14',
  surfaceAlt: '#12121c',
  border:     'rgba(201,168,76,0.2)',
  borderHi:   'rgba(201,168,76,0.5)',
  gold:       '#c9a84c',
  goldDim:    'rgba(201,168,76,0.12)',
  muted:      '#6b6a65',
  text:       '#e8e6e0',
};

const Avatar = ({ name, picture, size = 40 }) => {
  const initial = (name || '?').charAt(0).toUpperCase();
  if (picture) {
    return (
      <img
        src={picture}
        alt={name}
        style={{ width: size, height: size, border: `2px solid ${G.border}` }}
        className="rounded-full object-cover shrink-0"
        onError={e => { e.target.style.display = 'none'; }}
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size, background: G.goldDim, border: `1px solid ${G.border}`, color: G.gold, fontSize: size * 0.4 }}
      className="rounded-full flex items-center justify-center font-bold shrink-0"
    >
      {initial}
    </div>
  );
};

export { Avatar };

export const TeamChatModal = ({ orgId, orgName, currentUserId, currentUserRole, onClose }) => {
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [showMembersPanel, setShowMembersPanel] = useState(false);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const isOwnerOrAdmin = currentUserRole === 'owner' || currentUserRole === 'admin';

  const loadData = async () => {
    try {
      const [msgsRes, membRes] = await Promise.all([
        axios.get(`${API}/organizations/${orgId}/chat/messages`),
        axios.get(`${API}/organizations/${orgId}/members`)
      ]);
      setMessages(msgsRes.data || []);
      setMembers(membRes.data?.members || []);
    } catch (error) {
      console.error('Error loading team chat:', error);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, [orgId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!newMsg.trim() || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/organizations/${orgId}/chat/send`, { text: newMsg.trim() });
      setMessages([...messages, res.data]);
      setNewMsg('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar');
    }
    setSending(false);
  };

  const startPrivateChat = async (memberId, memberName) => {
    try {
      const res = await axios.post(`${API}/messages/conversations/create`, {
        other_user_id: memberId,
        initial_message: null
      });
      toast.success(`Chat con ${memberName} iniciado`);
      onClose();
      navigate('/messages', { state: { conversationId: res.data?.conversation_id } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar conversación');
    }
  };

  const handleKick = async (memberId, memberName) => {
    if (!window.confirm(`¿Expulsar a ${memberName} de la organización?`)) return;
    try {
      await axios.delete(`${API}/organizations/${orgId}/kick/${memberId}`);
      toast.success(`${memberName} expulsado`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al expulsar');
    }
  };

  const roleBadge = (role) => {
    if (role === 'owner') return '👑';
    if (role === 'admin') return '🛡️';
    if (role === 'leader') return '⭐';
    return '·';
  };

  return (
    <div
      data-testid="team-chat-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4"
      style={{ background: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl flex flex-col overflow-hidden"
        style={{
          height: '88vh',
          maxHeight: 680,
          background: G.surface,
          border: `1px solid ${G.borderHi}`,
          borderRadius: 20,
          boxShadow: `0 0 60px rgba(201,168,76,0.08), 0 30px 60px rgba(0,0,0,0.6)`,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 shrink-0"
          style={{ borderBottom: `1px solid ${G.border}` }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="flex items-center justify-center rounded-xl shrink-0"
              style={{ width: 40, height: 40, background: G.goldDim, border: `1px solid ${G.border}` }}
            >
              <Users size={18} style={{ color: G.gold }} />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-base truncate" style={{ color: G.text, fontFamily: 'var(--font-display)' }}>
                {orgName}
              </h3>
              <button
                data-testid="toggle-members-panel"
                onClick={() => setShowMembersPanel(!showMembersPanel)}
                className="text-xs hover:underline"
                style={{ color: G.gold }}
              >
                {members.length} miembro{members.length !== 1 ? 's' : ''} · {showMembersPanel ? 'Ver chat' : 'Ver miembros'}
              </button>
            </div>
          </div>
          <button
            data-testid="close-team-chat"
            onClick={onClose}
            className="p-2 rounded-lg transition-colors hover:bg-white/5 shrink-0"
            style={{ color: G.muted }}
          >
            <X size={18} />
          </button>
        </div>

        {!showMembersPanel ? (
          <>
            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: G.bg }}>
              {messages.length === 0 ? (
                <p className="text-center py-10 text-sm" style={{ color: G.muted }}>
                  Chat grupal de <span style={{ color: G.gold }}>{orgName}</span>.<br />
                  Sé el primero en escribir algo.
                </p>
              ) : (
                messages.map(msg => {
                  const isMe = msg.user_id === currentUserId;
                  return (
                    <div key={msg.message_id} className={`flex gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                      {!isMe && <Avatar name={msg.user_name} picture={msg.user_picture} size={30} />}
                      <div
                        className="max-w-[75%] rounded-2xl px-3 py-2"
                        style={isMe
                          ? { background: G.goldDim, border: `1px solid ${G.border}` }
                          : { background: G.surfaceAlt, border: `1px solid ${G.border}` }
                        }
                      >
                        {!isMe && (
                          <p className="text-xs font-semibold mb-1" style={{ color: G.gold }}>
                            {msg.user_name}
                          </p>
                        )}
                        <p className="text-sm whitespace-pre-wrap break-words" style={{ color: G.text }}>
                          {msg.text}
                        </p>
                        <p className="text-[10px] mt-1" style={{ color: G.muted }}>
                          {new Date(msg.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Input */}
            <div className="p-3 shrink-0" style={{ borderTop: `1px solid ${G.border}`, background: G.surface }}>
              <div className="flex gap-2">
                <input
                  data-testid="org-chat-input"
                  type="text"
                  value={newMsg}
                  onChange={e => setNewMsg(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSend()}
                  placeholder="Escribe un mensaje al equipo..."
                  className="flex-1 px-4 py-2.5 rounded-full text-sm outline-none"
                  style={{ background: G.bg, color: G.text, border: `1px solid ${G.border}` }}
                  onFocus={e => e.target.style.borderColor = G.gold}
                  onBlur={e => e.target.style.borderColor = G.border}
                />
                <button
                  data-testid="org-chat-send"
                  onClick={handleSend}
                  disabled={sending || !newMsg.trim()}
                  className="flex items-center justify-center rounded-full shrink-0 transition-opacity disabled:opacity-40"
                  style={{ width: 40, height: 40, background: G.gold }}
                >
                  <Send size={16} color="#050508" />
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Panel de miembros */
          <div className="flex-1 overflow-y-auto p-4 space-y-2" style={{ background: G.bg }}>
            <p className="text-xs mb-3" style={{ color: G.muted }}>
              Pulsa "Chat" para hablar en privado con un compañero.
            </p>
            {members.map(m => {
              const isMe = m.user_id === currentUserId;
              return (
                <div
                  key={m.user_id}
                  data-testid={`team-member-${m.user_id}`}
                  className="flex items-center justify-between p-3 rounded-xl"
                  style={{ background: G.surfaceAlt, border: `1px solid ${G.border}` }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar name={m.name} picture={m.picture} size={38} />
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate" style={{ color: G.text }}>
                        {m.name}{isMe && <span className="ml-1 text-xs" style={{ color: G.muted }}>(tú)</span>}
                      </p>
                      <p className="text-xs" style={{ color: G.gold }}>
                        {roleBadge(m.role)} {m.role === 'owner' ? 'Propietario' : m.role === 'admin' ? 'Admin' : m.role === 'leader' ? 'Líder' : 'Agente'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {!isMe && (
                      <button
                        data-testid={`chat-with-${m.user_id}`}
                        onClick={() => startPrivateChat(m.user_id, m.name)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-opacity hover:opacity-80"
                        style={{ background: G.goldDim, color: G.gold, border: `1px solid ${G.border}` }}
                        title="Chat privado"
                      >
                        <MessageCircle size={13} /> Chat
                      </button>
                    )}
                    {!isMe && isOwnerOrAdmin && m.role !== 'owner' && (
                      <button
                        data-testid={`kick-${m.user_id}`}
                        onClick={() => handleKick(m.user_id, m.name)}
                        className="p-1.5 rounded-lg transition-opacity hover:opacity-80"
                        style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}
                        title="Expulsar"
                      >
                        <UserX size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
